/**
 * Cheap "is the live playlist being served yet" check.
 *
 * Recovering from a playlist that 404'd because it had not been published yet
 * costs a full `load()`, which destroys and rebuilds the hls.js instance. Left
 * to run on every poll that is a teardown per poll - a broadcaster who takes a
 * minute to go live pays one a second. Probing first turns the retry loop into
 * one small request per poll and one rebuild, at the moment it will succeed.
 */

/**
 * How long to wait for a probe before treating it as unavailable.
 *
 * A connection the CDN accepts but never answers would otherwise leave the
 * probe pending indefinitely. The caller skips probing while one is in flight,
 * so an unbounded probe does not just delay one retry - it stops every later
 * poll from retrying at all, and a stopped hls.js instance has nothing else to
 * recover it. Comfortably above a healthy response, well under the 20s the
 * live playlist load policy allows.
 */
export const PLAYLIST_PROBE_TIMEOUT_MS = 5000;

/**
 * Whether the playlist at `url` is currently being served.
 *
 * A plain GET rather than HEAD: the manifest is a few hundred bytes, and a
 * HEAD that some layer of the delivery path answers with 405 would read as
 * "never available" and stall playback permanently. No custom headers and no
 * credentials, so this stays a CORS-simple request and never costs a
 * preflight.
 *
 * Any failure means "not ready" - including a rejected fetch, since a 404 from
 * the CDN need not carry CORS headers and is then indistinguishable from being
 * offline. Both are answered the same way: leave the retry outstanding and try
 * again on the next poll.
 */
export async function probeLivePlaylist(
  url: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = PLAYLIST_PROBE_TIMEOUT_MS,
): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(url, {
      cache: 'no-store',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
